/** Invite codes are always 6 uppercase alphanumeric characters. */
export const INVITE_CODE_LENGTH = 6;
/**
 * Alphabet for invite codes (no I/O/0/1 — avoids ambiguous glyphs). Generator
 * and format validation share this set.
 */
export const INVITE_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
/** Auto-start delay once every seated player is ready (ms). */
export const LOBBY_AUTO_START_MS = 3_000;
/** Watch-only connections a room admits on top of its player seats. */
export const MAX_SPECTATORS = 20;
/** Upper bound on the lobby's optional max-minutes-per-move setting. */
export const MAX_TURN_TIME_LIMIT_MINUTES = 24 * 60;
/**
 * Dispose a lobby that still has zero seated players after this grace period
 * (covers create-then-never-join and last-player-leave races).
 */
export const LOBBY_EMPTY_DISPOSE_MS = 45_000;
/**
 * How long a room whose only occupants are HELD seats (members who readied up
 * and left — their reservation never expires) stays alive with zero
 * connections before disposing. Bounds the room leak a never-filling lobby
 * (or a match everyone abandoned) would otherwise be. Disposing the ROOM does
 * not end the LOBBY: its record stays "waiting" and is revived into a fresh
 * room on demand — only starting or killing ends it.
 */
export const HELD_SEATS_EMPTY_DISPOSE_MS = 24 * 60 * 60 * 1000;
/**
 * Lobby-creation throttle: `POST /lobbies` is rejected once the creator
 * already has MORE THAN this many lobbies sitting in the "waiting" state in
 * the lobbies collection. (Guests have no account to count by and are exempt,
 * as is a server running without persistence.)
 */
export const MAX_WAITING_LOBBIES_PER_CREATOR = 2;
/**
 * How long a live match (setup/playing) holds a disconnected player's seat
 * open for reconnection, in seconds. A page refresh / app reload lands the
 * player back on the lobby hub, which offers "Reconnect" while this window is
 * open. Once it lapses, the transport token expires but the account-owned game
 * seat remains disconnected and can still be reclaimed through the hub or a
 * persisted-room resume.
 */
export const GAME_RECONNECT_GRACE_S = 120;
/**
 * WebSocket close code a server sends when it hands its rooms to a newer
 * server (a deploy superseded it, or it was told to shut down). NOT a
 * consented leave: the client should re-resolve the match straight away via
 * the room endpoint — a token reconnect could only reach the new server,
 * which never held this room. Clear of Colyseus' own codes (4000 consented,
 * 4002 error, 4010 dev-mode restart).
 */
export const WS_CLOSE_SERVER_SUPERSEDED = 4011;
/** Clamp a requested max-players value into the legal lobby range. */
export function clampLobbyMaxPlayers(raw, minPlayers = 2, absoluteMax = 8) {
    if (!Number.isFinite(raw))
        return absoluteMax;
    return Math.max(minPlayers, Math.min(absoluteMax, Math.floor(raw)));
}
/** Normalize an invite code for lookup / display (trim + uppercase). */
export function normalizeInviteCode(raw) {
    return raw.trim().toUpperCase();
}
/** Shape check for a typed invite code (after normalize) — matches generator. */
export function isValidInviteCodeFormat(code) {
    if (code.length !== INVITE_CODE_LENGTH)
        return false;
    for (const ch of code) {
        if (!INVITE_CODE_ALPHABET.includes(ch))
            return false;
    }
    return true;
}
/** Rows per match-history page (fixed server-side; echoed in the response). */
export const MATCH_HISTORY_PAGE_SIZE = 10;
//# sourceMappingURL=lobby.js.map